import { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import ReactEChartsCore from 'echarts-for-react/lib/core'
import { cn } from '@artprov/design-system'
import { echarts } from './echartsRegistry'
import { buildColumnChartOption } from './buildColumnChartOption'

const getRecommendedMinWidth = (categoryCount, seriesCount) => {
  let baseGroupWidth = 74

  if (categoryCount <= 6) {
    baseGroupWidth = 126
  } else if (categoryCount <= 8) {
    baseGroupWidth = 94
  }

  const extraSeriesWidth = Math.max(0, seriesCount - 3) * 10

  return Math.max(420, categoryCount * (baseGroupWidth + extraSeriesWidth) + 64)
}

export const ColumnChart = ({
  categories,
  series,
  height = 420,
  width = '100%',
  minWidth,
  barWidth,
  className = '',
  title,
  ariaLabel = 'Column chart',
}) => {
  const containerRef = useRef(null)
  const chartRef = useRef(null)
  const option = buildColumnChartOption({
    categories,
    series,
    title,
    barWidth,
  })
  const resolvedMinWidth = minWidth ?? getRecommendedMinWidth(categories.length, series.length)

  useEffect(() => {
    const container = containerRef.current

    if (!container) return

    const scheduleResize = () => {
      const raf = globalThis.requestAnimationFrame
        ? globalThis.requestAnimationFrame.bind(globalThis)
        : (callback) => {
            callback(0)
            return 0
          }

      raf(() => {
        chartRef.current?.getEchartsInstance?.()?.resize()
      })
    }

    let resizeObserver

    if (typeof ResizeObserver === 'function') {
      resizeObserver = new ResizeObserver(() => {
        scheduleResize()
      })
      resizeObserver.observe(container)
    } else {
      globalThis.addEventListener('resize', scheduleResize)
    }

    return () => {
      resizeObserver?.disconnect()
      globalThis.removeEventListener('resize', scheduleResize)
    }
  }, [])

  return (
    <figure
      ref={containerRef}
      className={cn('relative', className)}
      style={{ width, minWidth: resolvedMinWidth, height }}
    >
      <ReactEChartsCore
        ref={chartRef}
        echarts={echarts}
        option={option}
        notMerge
        lazyUpdate
        autoResize
        opts={{ renderer: 'svg' }}
        style={{ width: '100%', height: '100%' }}
      />
      <figcaption className="sr-only">{ariaLabel}</figcaption>
    </figure>
  )
}

ColumnChart.propTypes = {
  categories: PropTypes.arrayOf(PropTypes.string).isRequired,
  series: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      data: PropTypes.arrayOf(PropTypes.number).isRequired,
      color: PropTypes.string,
    })
  ).isRequired,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  minWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  barWidth: PropTypes.number,
  className: PropTypes.string,
  title: PropTypes.string,
  ariaLabel: PropTypes.string,
}

export default ColumnChart
